import { useCallback, useMemo } from 'react';

import { DEFAULT_USER_PREFERENCES } from '@personal-trainer-app/shared/src/types/user-preferences.types';

import type { WorkoutDay } from '@personal-trainer-app/shared/src/types/auth';
import type {
  UpdateUserPreferencesRequest,
  UserPreferences,
} from '@personal-trainer-app/shared/src/types/user-preferences.types';

import { useToast } from '@/components/ui';
import { useAuth } from '@/hooks/auth';
import { useUserPreferencesStore } from '@/stores';

/**
 * The API can return `null` for the open-ended maps and the preferred-days list
 * even though `UserPreferences` types them as non-null, so we treat those
 * fields as nullable while normalizing.
 */
type StoredPreferences = Omit<
  UserPreferences,
  'notification_preferences' | 'privacy_settings' | 'preferred_workout_days'
> & {
  notification_preferences: Record<string, boolean> | null;
  privacy_settings: Record<string, string | boolean> | null;
  preferred_workout_days: WorkoutDay[] | null;
};

export interface UseUserPreferencesReturn {
  /** Current preferences, fully normalized (no null maps/lists). */
  preferences: UserPreferences;
  /** Whether a persist call is in flight. */
  isUpdating: boolean;
  /**
   * Persist a partial preferences update. Shows a success/error toast and
   * resolves `true` on success, `false` on failure (so callers can revert
   * optimistic UI). Never throws.
   */
  savePreferences: (
    updates: UpdateUserPreferencesRequest,
    successMessage?: string
  ) => Promise<boolean>;
}

/**
 * Thin React wrapper over the shared user-preferences store. Reads the current
 * preferences off the auth user (kept in sync by the store), normalizes any
 * null fields, and exposes a toast-wrapped save for hybrid auto-save.
 */
export function useUserPreferences(): UseUserPreferencesReturn {
  const { user } = useAuth();
  const updatePreferences = useUserPreferencesStore(
    (state) => state.updatePreferences
  );
  const isUpdating = useUserPreferencesStore((state) => state.isUpdating);
  const { success, error: showErrorToast } = useToast();

  const stored = (user?.preferences ?? null) as StoredPreferences | null;

  // Coalesce null maps/lists (and any missing scalars) so every consumer can
  // read preferences without null checks. Memoized on `stored` so reference
  // identity stays stable between renders (important for optimistic controls).
  const preferences = useMemo<UserPreferences>(
    () => ({
      theme_selection:
        stored?.theme_selection ?? DEFAULT_USER_PREFERENCES.theme_selection,
      measurement_system:
        stored?.measurement_system ??
        DEFAULT_USER_PREFERENCES.measurement_system,
      language: stored?.language ?? DEFAULT_USER_PREFERENCES.language,
      dashboard_profile:
        stored?.dashboard_profile ?? DEFAULT_USER_PREFERENCES.dashboard_profile,
      workouts_per_week: stored?.workouts_per_week ?? null,
      preferred_workout_days: stored?.preferred_workout_days ?? [],
      notification_preferences: stored?.notification_preferences ?? {},
      privacy_settings: stored?.privacy_settings ?? {},
      detected_timezone: stored?.detected_timezone ?? null,
    }),
    [stored]
  );

  const savePreferences = useCallback(
    async (
      updates: UpdateUserPreferencesRequest,
      successMessage = 'Saved'
    ): Promise<boolean> => {
      try {
        await updatePreferences(updates);
        success(successMessage, { duration: 1800 });
        return true;
      } catch {
        showErrorToast('Couldn’t save your change. Please try again.');
        return false;
      }
    },
    [updatePreferences, success, showErrorToast]
  );

  return { preferences, isUpdating, savePreferences };
}
